import './DevicePanel.scss';
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  ReactElement
} from 'react';
import {IoChevronDown, IoChevronUp} from 'react-icons/io5';
import moment from 'moment';
import StatusControl from '../shared/StatusControl/StatusControl';
import {
  Loader,
  Panel,
  IconButton,
  Whisper,
  Tooltip,
  toaster
} from 'rsuite';
import {Reload} from '@rsuite/icons';
import {convertCoordinatesToDecimal} from '../menu/MainPage/MainPage.utils';
import {useWeather} from '../../hooks/useWeather';
import {ClockStation} from '../../Targets';
import {ReadAstronomicRelay} from '../../Commands';
import WeatherSensors from '../shared/WeatherSensors/WeatherSensors';
import {BksApi} from '../../services/Bks.api';
import {SharedApi} from '../../services/Shared.api';
import NotificationFactory from '../../shared/Notifications';
import DevicesModal from '../modals/DeviceModal/DeviceModal';
import {DeviceUtils} from "../../shared/Device.utils";
import {BsActivity} from "react-icons/bs";
import {RiToolsIcon} from "../../models/Icons.model";
import {needsMaintenance} from "../shared/VerboxChat/maintenanceChat";
import { IconBaseProps } from "react-icons";
import {
  DecimalCoordinates,
  DevicePanelDevice,
  DevicePanelProps,
  DeviceStatus,
  ExecuteCommandResult,
  MeteoDashboardData,
  MeteoDashboardResponse,
  MeteoDevice,
  MeteoSource,
  RelayData,
  WeatherData
} from "./DevicePanel.model";

type IconType = (props: IconBaseProps) => ReactElement;
const BsActivityIcon = BsActivity as unknown as IconType;
const IoChevronUpIcon = IoChevronUp as unknown as IconType;
const IoChevronDownIcon = IoChevronDown as unknown as IconType;

const DevicePanel = ({
                       device,
                       dawnTime,
                       duskTime,
                       onDeviceSelect,
                       availableDevices = [],
                       onRefreshDevice,
                       isAdmin = false,
                       executeCommand
                     }: DevicePanelProps) => {
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus | null>(null);
  const [showDeviceList, setShowDeviceList] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [relayData, setRelayData] = useState<RelayData | null>(null);
  const [bksDawnTime, setBksDawnTime] = useState<string | null>(null);
  const [bksDuskTime, setBksDuskTime] = useState<string | null>(null);
  const [meteoData, setMeteoData] = useState<MeteoDashboardData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [meteoSource, setMeteoSource] = useState<MeteoSource | null>(null);

  const isTimePoint = (value?: string | { hours?: number; minutes?: number } | null): value is { hours?: number; minutes?: number } => {
    return typeof value === 'object' && value !== null;
  };

  const handleOpenLinkedModal = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleSelectDevice = async (meteoDevice: MeteoDevice) => {
    if (!device?.id) return;

    try {
      const selectedId = meteoDevice?.id === 'coordinates' ? null : meteoDevice?.id;

      const result = await SharedApi.updateMeteoDataDevice(
        String(device.id),
        selectedId as unknown as string
      );

      if (meteoDevice?.id === 'coordinates') {
        setMeteoSource({
          type: 'coords',
          model: 'По координатам',
          address: null
        });
      }

      if (result) {
        await getMeteoDashboard();
        toaster.push(NotificationFactory.success(), {
          placement: 'topEnd'
        });
      }
    } catch (err) {
      console.error(err);
      toaster.push(NotificationFactory.error(), {placement: 'topEnd'});
    }
  };


  const formatTimeHHmm = useCallback((timeString?: string | null) => {
    return timeString ? timeString.slice(0, 5) : null;
  }, []);

  const getTime = useCallback(async () => {
    if (!device?.id) return null;

    try {
      const response = await BksApi.getTime(String(device?.id));
      setBksDawnTime(formatTimeHHmm(response?.dawnTime));
      setBksDuskTime(formatTimeHHmm(response?.duskTime));
    } catch (error) {
      console.error(error);
    }
  }, [device?.id, formatTimeHHmm]);

  const getMeteoDashboard = useCallback(async () => {
    if (!device?.id) return;

    try {
      const response = await SharedApi.getMeteoData(String(device.id)) as MeteoDashboardResponse;

      if (!response || response.preferredDeviceId == null || !response.meteoData) {
        setMeteoData(null);
        setMeteoSource(null);
        return;
      }

      setMeteoData(response.meteoData);
      setMeteoSource(response.meteoData.source ?? null);
    } catch (err) {
      console.error(err);
    }
  }, [device?.id]);


  useEffect(() => {
    if (!device?.isClockStation) {
      getTime();
    }
    getMeteoDashboard();
  }, [device?.id, getTime, getMeteoDashboard]);

  const decimalCoordinates = useMemo<DecimalCoordinates | null>(() => {
    if (!relayData?.latitude || !relayData?.longitude) {
      return null;
    }

    return convertCoordinatesToDecimal(relayData) as DecimalCoordinates;
  }, [
    relayData?.latitude,
    relayData?.longitude,
    relayData?.latitudeDirection,
    relayData?.longitudeDirection
  ]);

  const {
    weatherData,
    loading: weatherLoading,
    refreshWeather
  } = useWeather(decimalCoordinates) as {
    weatherData: WeatherData | null;
    loading: boolean;
    refreshWeather: () => void;
  };

  const currentDevice = useMemo<DevicePanelDevice | null>(() => {
    if (!device) return null;

    return {
      ...device,
      dawnTime,
      duskTime,
    };
  }, [device, dawnTime, duskTime]);

  useEffect(() => {
    if (isAdmin) {
      if (device) {
        setDeviceStatus({isConnected: device.isConnected});
      }
    } else {
      if (device?.id && !showDeviceList) {
        getStatusInfo(device.id);
      }
    }
  }, [device?.id, device?.isConnected, isAdmin, showDeviceList]);

  const getStatusInfo = useCallback(async (deviceGuid: string | number) => {
    try {
      const statusData = await SharedApi.getControlBlocksStatus(String(deviceGuid)) as DeviceStatus;
      setDeviceStatus(statusData);
    } catch (err) {
      console.error('Ошибка при получении статуса устройства:', err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setShowDeviceList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAdmin]);

  useEffect(() => {
    const getRelayData = async () => {
      try {
        const result = await executeCommand(
          ClockStation,
          ReadAstronomicRelay,
          []
        ) as ExecuteCommandResult;
        const params = result.Params;
        const utf8decoder = new TextDecoder();
        const buffer = new Uint8Array(result.Params.slice(2, 18));
        const text = utf8decoder.decode(buffer);

        const relayData = {
          isManual: !!params[1],
          latitudeDirection: text[0],
          latitude: text.slice(1, 8),
          longitudeDirection: text[8],
          longitude: text.slice(9, 16)
        };

        setRelayData(relayData);
      } catch (e) {
        console.error(e);
      }
    };

    if (dawnTime || duskTime) {
      getRelayData().then();
    }

  }, [dawnTime, duskTime, bksDawnTime, bksDuskTime]);

  const toggleDeviceList = () => {
    if (isAdmin) return;
    setShowDeviceList((prev) => !prev);
  };

  const handleDeviceSelect = (selectedDevice: DevicePanelDevice) => {
    onDeviceSelect(selectedDevice);
    setShowDeviceList(false);
  };

  const handleRefresh = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!device?.id || isRefreshing) return;

    setIsRefreshing(true);
    try {
      if (onRefreshDevice) {
        await onRefreshDevice(device.id);
      }
      if (!isAdmin) {
        await getStatusInfo(device.id);
      }
    } catch (error) {
      console.error('Ошибка при обновлении устройства:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getUrlImg = () => {
    const imgDevice = isAdmin ? currentDevice : device;
    if (!imgDevice?.previewImagePath) return null;
    if (imgDevice.previewImagePath.startsWith('http')) {
      return imgDevice.previewImagePath;
    }
    const baseUrl = window.location.origin;
    return `${baseUrl}/${imgDevice.previewImagePath}`;
  };

  const displayStatus = isAdmin
    ? currentDevice?.isConnected
    : deviceStatus?.isConnected;

  const displayDawnTime = useMemo(
    () => currentDevice?.dawnTime,
    [currentDevice]
  );
  const displayDuskTime = useMemo(
    () => currentDevice?.duskTime,
    [currentDevice]
  );

  const dawnTimeString = useMemo(() => {
    if (!isTimePoint(currentDevice?.dawnTime)) return null;
    const timePoint = currentDevice?.dawnTime;
    if (!timePoint || typeof timePoint === 'string') return null;
    return `${timePoint.hours}:${timePoint.minutes}`;
  }, [currentDevice?.dawnTime]);

  const duskTimeString = useMemo(() => {
    if (!isTimePoint(currentDevice?.duskTime)) return null;
    const timePoint = currentDevice?.duskTime;
    if (!timePoint || typeof timePoint === 'string') return null;
    return `${timePoint.hours}:${timePoint.minutes}`;
  }, [currentDevice?.duskTime]);

  useEffect(() => {
    if (decimalCoordinates && (dawnTimeString || duskTimeString)) {
      refreshWeather();
    }
  }, [dawnTimeString, duskTimeString, decimalCoordinates, refreshWeather]);

  const getMeteoPrefix = (model?: string | null) => {
    if (!model) return '';

    const modelLower = model.toLowerCase();
    if (modelLower.includes('naos')) return 'V';
    if (modelLower.includes('titan')) return 'M';
    if (modelLower.includes('altair.s')) return 'S';
    if (modelLower.includes('altair.d')) return 'D';

    return '';
  };

  const prefix = getMeteoPrefix(meteoSource?.model);

  const meteoLabel =
    !meteoSource
      ? 'Относительно координат'
      : meteoSource.type === 'coords'
        ? 'по координатам'
        : `${
          (meteoSource.type === 'ClockMechanicE' || meteoSource.type === 'ClockMechanicV' || meteoSource.type === 'ClockMechanicN')
            ? 'Умный час. механизм'
            : 'Блок датчиков'
        } | ${prefix}${(meteoSource.address || 0)
          .toString()
          .padStart(4, '0')}`;

  const sensorsData = meteoData;
  const sensorsLoading = weatherLoading && !meteoData;

  return (
    <div className="device-panel" ref={panelRef}>
      <Panel
        bodyFill
        bordered
        style={{
          background: '#fff',
          cursor: isAdmin ? 'default' : 'pointer'
        }}
        onClick={isAdmin ? undefined : toggleDeviceList}
      >
        <div className="panel-content">
          <div className="device-panel__header">
            <div className="image-container">
              <div className="image-wrapper">
                <img src={getUrlImg() || 'clock.svg'} alt="clock-img"/>
              </div>
            </div>
            <div className="device-panel__info">
              <div className="device-panel__primary">
                <div className="info-item serial-numbers">
                  <div className="serial-item">
                    <p className="table-title">ЭМЧС</p>
                    <p
                      className="table-val"
                      style={{
                        color:
                          !currentDevice?.clockStation
                            ?.clockSystemSerialNumber &&
                          !currentDevice?.clockSystemSerialNumber
                            ? '#CCCCCC'
                            : '#0A4270'
                      }}
                    >
                      {currentDevice?.clockStation
                        ? currentDevice?.clockStation
                        ?.clockSystemSerialNumber || '000000'
                        : currentDevice?.clockSystemSerialNumber}
                    </p>
                  </div>
                  <div className="serial-item">
                    <p className="table-title">ЧС</p>
                    <p
                      className="table-val"
                      style={{
                        color:
                          !currentDevice?.clockStation?.serialNumber &&
                          !currentDevice?.serialNumber
                            ? '#CCCCCC'
                            : '#0A4270'
                      }}
                    >
                      {currentDevice?.clockStation
                        ? currentDevice?.clockStation?.serialNumber ||
                        'R0000'
                        : currentDevice?.serialNumber}
                    </p>
                  </div>
                </div>
                <div className="info-item description">
                  <span className="device-panel__description ellipsis">
                    {currentDevice?.description || ''}
                  </span>
                </div>
                <div>
                  <div className="d-flex">
                    <div className="info-item sun-times me-3">
                      <div className="sun-item">
                        <p className="table-title">Восход</p>
                        {!device?.clockStation ? (
                          <p className="table-val">{bksDawnTime}</p>
                        ) : (
                          <p className="table-val">
                            {displayDawnTime !== null ? (
                              `${displayDawnTime}`
                            ) : (
                              <span className="grey">00:00</span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="sun-item">
                        <p className="table-title">Закат</p>
                        {!device?.clockStation ? (
                          <p className="table-val">{bksDuskTime}</p>
                        ) : (
                          <p className="table-val">
                            {displayDuskTime !== null ? (
                              `${displayDuskTime}`
                            ) : (
                              <span className="grey">00:00</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="info-item sensors">
                      <WeatherSensors
                        meteoData={sensorsData as any}
                        weatherData={weatherData}
                        loading={sensorsLoading}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="info-item meteo-label d-flex justify-content-end col">
                      <span
                        style={{
                          color: '#666',
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                        onClick={handleOpenLinkedModal}
                      >
                        {meteoLabel}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="info-item status-controls">
                  <div className="online-status">
                    <p className="table-title">Онлайн</p>
                    <p className="table-val online-time">
                      {moment(currentDevice?.lastOnlineTime).fromNow()}
                    </p>
                  </div>
                  <div className="connection-controls text-end">
                    <div className="d-flex align-items-center device-panel__status-row">
                      <Whisper placement="top" speaker={
                        <Tooltip>{moment(device?.lastOnlineTime || '').fromNow()}</Tooltip>
                      }>
                        <IconButton
                          className={`${DeviceUtils.getActivityClassName(device?.lastOnlineTime || '')} device-panel__activity-btn`}
                          icon={<BsActivityIcon />}
                          size="xs"
                        />
                      </Whisper>
                      <StatusControl
                        status={displayStatus ? 'online' : 'offline'}
                      />

                    </div>
                    <div className="d-flex align-items-center text-end device-panel__action-row">
                      {needsMaintenance(currentDevice?.nextMaintenanceDate) && (
                        <Whisper
                          placement="top"
                          speaker={<Tooltip>Пройдите ТО. Обратитесь в сервисный центр</Tooltip>}
                        >
                          <span className="device-panel__maintenance" aria-label="Требуется ТО" tabIndex={0}>
                            <RiToolsIcon />
                          </span>
                        </Whisper>
                      )}
                      <Whisper
                        placement="top"
                        speaker={
                          <Tooltip>Обновить данные устройства</Tooltip>
                        }
                      >
                        <IconButton
                          icon={<Reload/>}
                          size="sm"
                          loading={isRefreshing}
                          onClick={handleRefresh}
                          className="refresh-btn"
                        />
                      </Whisper>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {!isAdmin && (
              <div className="device-panel__controls">
                <div
                  className="device-panel__chevron"
                  title="Выбрать устройство"
                >
                  {showDeviceList ? (
                    <IoChevronUpIcon size={20}/>
                  ) : (
                    <IoChevronDownIcon size={20}/>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Panel>

      {!isAdmin && showDeviceList && (
        <Panel bodyFill bordered className="device-dropdown">
          <div className="device-list">
            <div className="device-list__content">
              {availableDevices?.length ? (
                availableDevices.map((dev) => (
                  <div
                    key={dev.id}
                    className={`device-list__item ${
                      device?.id === dev.id ? 'selected' : ''
                    }`}
                    onClick={() => handleDeviceSelect(dev)}
                  >
                    <div className="panel-content">
                      <div className="device-panel__header">
                        <img
                          style={{
                            width: 100,
                            height: 80,
                            borderRadius: 5,
                            overflow: 'hidden'
                          }}
                          src={
                            dev?.previewImagePath
                              ? `${window.location.origin}/${dev.previewImagePath}`
                              : 'clock.svg'
                          }
                          alt="clock-img"
                        />

                        <div className="device-list-item__info">
                          <div className="device-list-item__primary">
                            <div className="info-item serial-numbers">
                              <div className="serial-item">
                                <p className="table-title">ЭМЧС</p>
                                <p
                                  className="table-val"
                                  style={{
                                    color: !dev?.clockStation
                                      ?.clockSystemSerialNumber
                                      ? '#CCCCCC'
                                      : '#0A4270'
                                  }}
                                >
                                  {dev?.clockStation
                                    ?.clockSystemSerialNumber || '000000'}
                                </p>
                              </div>
                              <div className="serial-item">
                                <p className="table-title">ЧС</p>
                                <p
                                  className="table-val"
                                  style={{
                                    color: !dev?.clockStation?.serialNumber
                                      ? '#CCCCCC'
                                      : '#0A4270'
                                  }}
                                >
                                  {dev?.clockStation?.serialNumber ||
                                    'R0000'}
                                </p>
                              </div>
                            </div>

                            <div className="info-item description">
                              <span className="device-list-item__description ellipsis">
                                {dev?.description || ''}
                              </span>
                            </div>

                            <div className="info-item sun-times">
                              <div className="sun-item">
                                <p className="table-title">Восход</p>
                                <p className="table-val">
                                  {isTimePoint(dev?.duskTime) &&
                                  dev?.duskTime?.hours &&
                                  dev?.duskTime?.minutes ? (
                                    `${dev?.duskTime?.hours
                                      ?.toString()
                                      ?.padStart(2, '0') || '00'}:${dev?.duskTime?.minutes
                                      ?.toString()
                                      .padStart(2, '0') || '00'}`
                                  ) : (
                                    <span className="grey">00:00</span>
                                  )}
                                </p>
                              </div>
                              <div className="sun-item">
                                <p className="table-title">Закат</p>
                                <p className="table-val">
                                  {isTimePoint(dev?.dawnTime) &&
                                  dev?.dawnTime?.hours &&
                                  dev?.dawnTime?.minutes ? (
                                    `${dev?.dawnTime?.hours
                                      ?.toString()
                                      ?.padStart(2, '0') || '00'}:${dev?.dawnTime?.minutes
                                      ?.toString()
                                      .padStart(2, '0') || '00'}`
                                  ) : (
                                    <span className="grey">00:00</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="info-item sensors">
                              <WeatherSensors
                                meteoData={sensorsData as any}
                                weatherData={weatherData}
                                loading={sensorsLoading}
                              />
                            </div>

                            <div className="info-item status-controls">
                              <div className="online-status">
                                <p className="table-title">Онлайн</p>
                                <p className="table-val online-time">
                                  {moment(dev?.lastOnlineTime).fromNow()}
                                </p>
                              </div>
                              <div className="connection-controls">
                                <StatusControl
                                  status={
                                    dev?.isConnected ? 'online' : 'offline'
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{textAlign: 'center'}}>
                  <Loader/>
                </div>
              )}
            </div>
          </div>
        </Panel>
      )}

      <DevicesModal
        open={isModalOpen}
        deviceId={device?.id}
        onClose={() => setIsModalOpen(false)}
        onSelectDevice={handleSelectDevice}
      />
    </div>
  );
};

export default DevicePanel;
